import { useEffect, useMemo, useState } from "react";
import { Modal, Form, Button, Spinner, Alert } from "react-bootstrap";
import { JourneyService } from "@services/journey/journeyService";
import { JourneySummary } from "@services/journey/commands/cmdListJourneys";
import { CopyJourneyStepsPayload } from "@services/journey/commands/cmdCopyJourneySteps";

const service = new JourneyService();

// "phase" = copy phases into the journey root. "step" = copy a phase's steps into a phase.
export type CopyLevel = "phase" | "step";

interface StepNode {
  id: number;
  name: string;
  sequence: number;
  children?: StepNode[];
}

// The steps API returns siblings ordered by name; the tree is authored by sequence.
// Sort here so copies preserve the source's real order (the main editor does the same).
function bySequence(nodes: StepNode[]): StepNode[] {
  return [...nodes].sort((a, b) => a.sequence - b.sequence || a.name.localeCompare(b.name));
}

const DEPTH_OPTIONS: Record<CopyLevel, { value: number; label: string }[]> = {
  phase: [
    { value: 1, label: "Phases only" },
    { value: 2, label: "Phases + Steps" },
    { value: 3, label: "Everything (Phases + Steps + Substeps)" },
  ],
  step: [
    { value: 1, label: "Steps only" },
    { value: 2, label: "Steps + Substeps" },
  ],
};

interface Props {
  show: boolean;
  level: CopyLevel;
  /** The journey we're copying INTO — excluded from the source list. */
  currentJourneyId: number;
  /** Container the copies land in (a phase id), or null for the journey root. */
  targetParentId: number | null;
  /** Name of the container, for the modal subtitle. */
  targetLabel?: string;
  onClose: () => void;
  onCopy: (payload: CopyJourneyStepsPayload) => Promise<void>;
}

export default function CopyFromModal({
  show, level, currentJourneyId, targetParentId, targetLabel, onClose, onCopy,
}: Props) {
  const [journeys, setJourneys] = useState<JourneySummary[]>([]);
  const [tree, setTree] = useState<StepNode[]>([]);
  const [sourceJourneyId, setSourceJourneyId] = useState<number | null>(null);
  const [sourcePhaseId, setSourcePhaseId] = useState<number | null>(null); // "step" level: which phase's steps
  const [sourceStepId, setSourceStepId] = useState<number | null>(null);   // null = all steps of that phase
  const depthOptions = DEPTH_OPTIONS[level];
  const [depth, setDepth] = useState<number>(depthOptions[depthOptions.length - 1].value);
  const [loadingTree, setLoadingTree] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!show) return;
    setSourceJourneyId(null);
    setSourcePhaseId(null);
    setSourceStepId(null);
    setTree([]);
    setDepth(depthOptions[depthOptions.length - 1].value);
    setError(null);
    service
      .listJourneys()
      .then((all) => setJourneys(all.filter((j) => j.id !== currentJourneyId)))
      .catch(() => setError("Failed to load journeys."));
  }, [show, currentJourneyId]);

  // Load the chosen source journey's tree so the user can pick a phase (and step).
  useEffect(() => {
    setSourcePhaseId(null);
    setSourceStepId(null);
    if (sourceJourneyId == null) {
      setTree([]);
      return;
    }
    setLoadingTree(true);
    service
      .getJourneySteps(sourceJourneyId)
      .then((t) => setTree(t ?? []))
      .catch(() => setError("Failed to load the source's phases."))
      .finally(() => setLoadingTree(false));
  }, [sourceJourneyId]);

  const byIndustry = useMemo(() => {
    const map = new Map<string, JourneySummary[]>();
    for (const j of journeys) {
      const key = j.industry || "Other";
      map.set(key, [...(map.get(key) ?? []), j]);
    }
    return [...map.entries()];
  }, [journeys]);

  const phases = useMemo(() => bySequence(tree), [tree]);
  const steps = useMemo(
    () => (sourcePhaseId == null ? [] : bySequence(phases.find((p) => p.id === sourcePhaseId)?.children ?? [])),
    [phases, sourcePhaseId],
  );

  const canCopy =
    !loadingTree &&
    !saving &&
    sourceJourneyId != null &&
    (level === "phase" ? phases.length > 0 : sourcePhaseId != null);

  const resolveSourceIds = (): number[] => {
    if (level === "phase") {
      return sourcePhaseId != null ? [sourcePhaseId] : phases.map((p) => p.id);
    }
    // step level
    if (sourceStepId != null) return [sourceStepId];
    return steps.map((s) => s.id);
  };

  const handleCopy = async () => {
    const ids = resolveSourceIds();
    if (ids.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await onCopy({ source_step_ids: ids, target_parent_id: targetParentId, depth });
      onClose();
    } catch {
      setError("Failed to copy. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const title = level === "step" ? "Copy steps from…" : "Copy from…";

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title as="h5" className="mb-0">
          {title}
          {targetLabel && <span className="text-secondary fw-normal"> into {targetLabel}</span>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold small text-uppercase text-secondary">Source journey</Form.Label>
          <Form.Select
            value={sourceJourneyId ?? ""}
            onChange={(e) => setSourceJourneyId(e.target.value ? Number(e.target.value) : null)}
            autoFocus
          >
            <option value="">Select a journey…</option>
            {byIndustry.map(([industry, list]) => (
              <optgroup key={industry} label={industry}>
                {list.map((j) => (
                  <option key={j.id} value={j.id}>{j.name}</option>
                ))}
              </optgroup>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold small text-uppercase text-secondary">
            {level === "step" ? "From phase" : "Phases"}
          </Form.Label>
          <Form.Select
            value={sourcePhaseId ?? ""}
            disabled={sourceJourneyId == null || loadingTree}
            onChange={(e) => {
              setSourcePhaseId(e.target.value ? Number(e.target.value) : null);
              setSourceStepId(null);
            }}
          >
            {/* Phase level allows "All phases"; step level needs a specific phase. */}
            <option value="">{level === "step" ? "Select a phase…" : "All phases"}</option>
            {phases.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Form.Select>
          {sourceJourneyId != null && !loadingTree && phases.length === 0 && (
            <div className="small text-secondary mt-1">This journey has no phases to copy.</div>
          )}
        </Form.Group>

        {level === "step" && (
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold small text-uppercase text-secondary">Steps</Form.Label>
            <Form.Select
              value={sourceStepId ?? ""}
              disabled={sourcePhaseId == null}
              onChange={(e) => setSourceStepId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">All steps</option>
              {steps.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </Form.Select>
            {sourcePhaseId != null && steps.length === 0 && (
              <div className="small text-secondary mt-1">This phase has no steps to copy.</div>
            )}
          </Form.Group>
        )}

        <Form.Group>
          <Form.Label className="fw-semibold small text-uppercase text-secondary">Include</Form.Label>
          {depthOptions.map((opt) => (
            <Form.Check
              key={opt.value}
              type="radio"
              name="copy-depth"
              id={`copy-depth-${opt.value}`}
              label={opt.label}
              checked={depth === opt.value}
              onChange={() => setDepth(opt.value)}
            />
          ))}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={handleCopy} disabled={!canCopy}>
          {saving ? <><Spinner size="sm" animation="border" className="me-2" />Copying…</> : "Copy"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
